/**
 * Login page for Catty's Microsoft SSO flow.
 *
 * Renders sign-in actions and contextual messaging for admin-consent and
 * callback outcomes returned as query parameters.
 */
import { Link } from 'react-router-dom';
import { buildAuthUrl, buildAdminConsentUrl } from '../config/auth';
import logoUrl from '../assets/logo.png';

export default function Login() {
  const params = new URLSearchParams(window.location.search);
  const consentGranted = params.get('consent_granted') === 'true';
  const consentError   = params.get('consent_error');
  const authError = params.get('auth_error');

  function authErrorMessage(code: string | null): string | null {
    if (code === 'missing_permissions') {
      return 'Your organisation has not granted the required Microsoft 365 permissions yet. Ask a Microsoft 365 admin to complete the access approval step, then try signing in again.';
    }
    if (code === 'code_redeemed' || code === 'invalid_grant') {
      return 'This sign-in attempt has expired or was already used. Please click Sign in with Microsoft again.';
    }
    if (code === 'consent_required') {
      return 'Your organisation has not approved Catty yet. Ask your Microsoft 365 administrator to approve the app, then try signing in again.';
    }
    if (code) {
      return 'Sign-in could not be completed. Please try again.';
    }
    return null;
  }

  const authErrorText = authErrorMessage(authError);

  const handleSignIn = () => {
    const plan = new URLSearchParams(window.location.search).get('plan');
    if (plan && ['trial', 'solo', 'team'].includes(plan)) {
      sessionStorage.setItem('catty_post_auth_plan', plan);
    }
    window.location.href = buildAuthUrl();
  };

  const appVersionLong = import.meta.env.VITE_APP_VERSION ?? '3.0.0-dev';
  const appVersionShort = appVersionLong.split('-')[0];

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      <div className="card shadow-sm" style={{ maxWidth: 400, width: '100%' }}>
        <div className="card-body p-5 text-center">
          <img src={logoUrl} alt="Catty" width={737} height={759} className="mb-3" style={{ width: '50%', height: 'auto' }} />
          <p className="text-muted">Email categorisation for your team</p>

          {consentGranted && (
            <div className="alert alert-success text-start mb-3" role="alert">
              <strong>Access approved.</strong>
              <p className="mb-1 mt-1" style={{ fontSize: '0.875rem' }}>
                Your organisation has granted Catty the necessary permissions.
              </p>
              <p className="mb-0" style={{ fontSize: '0.875rem' }}>
                Sign in below with your Microsoft account to continue.
              </p>
            </div>
          )}

          {consentError && (
            <div className="alert alert-warning text-start mb-3" role="alert">
              <strong>Consent not completed.</strong>
              <p className="mb-0 mt-1" style={{ fontSize: '0.875rem' }}>
                The admin approval step was cancelled or encountered an error. Please ask your
                Microsoft 365 administrator to grant access before signing in.
              </p>
            </div>
          )}

          {authErrorText && (
            <div className="alert alert-warning text-start mb-3" role="alert">
              <strong>Sign-in needs attention.</strong>
              <p className="mb-0 mt-1" style={{ fontSize: '0.875rem' }}>
                {authErrorText}
              </p>
            </div>
          )}

          <button
            className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2 mb-3"
            onClick={handleSignIn}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 23 23" fill="none" aria-hidden="true">
              <rect x="1" y="1" width="10" height="10" fill="#f25022" />
              <rect x="12" y="1" width="10" height="10" fill="#7fba00" />
              <rect x="1" y="12" width="10" height="10" fill="#00a4ef" />
              <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
            </svg>
            Sign in with Microsoft
          </button>

          <p className="mb-3" style={{ fontSize: '0.8rem' }}>
            <a
              href={buildAdminConsentUrl()}
              className="text-muted"
              style={{ textDecoration: 'none' }}
            >
              To approve Catty in your organisation, click here
            </a>
          </p>

          <p title={appVersionLong} className="text-muted mb-0" style={{ fontSize: '0.75rem', color: "var(--bs-secondary-border-subtle)" }}>
            {appVersionShort}
          </p>

          <p className="mb-0 mt-2" style={{ fontSize: '0.75rem' }}>
            <Link to="/terms" className="text-muted" style={{ textDecoration: 'none' }}>Terms</Link>
            {' · '}
            <Link to="/privacy" className="text-muted" style={{ textDecoration: 'none' }}>Privacy</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
